<template>
    <div class="js-cupertinoPane">
        <div class="station-hover-summary" ref="paneContent" :class="{ 'is-pane': hasCupertinoPane }" v-if="viewingSummary && station">
            <StationSummaryContent ref="summaryContent" :station="station">
                <template #top-right-actions>
                    <img :alt="$tc('iconAlts.close')" src="@/assets/icon-close.svg" class="close-button" v-on:click="wantCloseSummary" />
                    <img
                        :alt="$tc('station.navigateToStation')"
                        class="navigate-button"
                        :src="$loadAsset(interpolatePartner('tooltip-') + '.svg')"
                        @click="openStationPageTab"
                    />
                </template>
            </StationSummaryContent>

            <template v-if="isPartnerCustomisationEnabled()">
                <div class="latest-primary" :style="{ color: latestPrimaryColor }">
                    <template v-if="isDownStation(station)">
                        {{ $t("map.legend.maintenance") }}
                    </template>
                    <template v-else-if="isCommunityStation(station)">
                        {{ $t("map.legend.community") }}
                    </template>
                    <template v-else-if="isRetiredStation(station)">
                        {{ $t("map.legend.retired") }}
                    </template>
                    <template v-else>
                        <template v-if="latestPrimaryLevel !== null">{{ latestPrimaryLevel }}</template>
                        <span v-else-if="hasData" class="no-data">{{ $t("noRecentData") }}</span>
                        <span v-else class="no-data">{{ $t("noData") }}</span>
                    </template>

                    <i
                        :class="[
                            { 'community-dot': isCommunityStation(station) },
                            { 'down-dot': isDownStation(station) || isRetiredStation(station) },
                            dotSizeClass,
                        ]"
                        :style="getDotStyle()"
                    >
                        <template v-if="isDownStation(station)"></template>
                        <template v-else-if="isCommunityStation(station) || isRetiredStation(station)"></template>
                        <template v-else>{{ formatReadingForDisplay }}</template>
                    </i>
                </div>
            </template>

            <slot :station="station" :sensorDataQuerier="sensorDataQuerier"></slot>

            <div class="explore-button" v-if="explore" v-on:click="onClickExplore">{{ $t("station.exploreData") }}</div>

            <StationBattery :station="station" />
        </div>
    </div>
</template>

<script lang="ts">
import _ from "lodash";

import Vue, { PropType } from "vue";

import CommonComponents from "@/views/shared";
import StationBattery from "@/views/station/StationBattery.vue";
import StationSummaryContent from "./StationSummaryContent.vue";

import { ModuleSensorMeta, SensorDataQuerier, SensorMeta } from "@/views/shared/sensor_data_querier";
import { VisibleReadings, DecoratedReading } from "@/store";

import { getBatteryIcon } from "@/utilities";
import { BookmarkFactory, ExploreContext, serializeBookmark } from "@/views/viz/viz";
import { getPartnerCustomizationWithDefault, interpolatePartner, isCustomisationEnabled } from "./partners";
import { withBasePath } from "@/embed/embed-router";
import { StationStatus } from "@/api";
import { CupertinoPane } from "cupertino-pane";
import { isDownStation, isRetiredStation, isCommunityStation } from "@/utilities/station-status";

export default Vue.extend({
    name: "StationHoverSummary",
    components: {
        ...CommonComponents,
        StationSummaryContent,
        StationBattery,
    },
    props: {
        station: {
            type: Object,
            required: true,
        },
        explore: {
            type: Boolean,
            default: true,
        },
        exploreContext: {
            type: Object as PropType<ExploreContext>,
            default: () => {
                return new ExploreContext();
            },
        },
        sensorDataQuerier: {
            type: Object as PropType<SensorDataQuerier>,
            required: false,
        },
        visibleReadings: {
            type: Number as PropType<VisibleReadings>,
            default: VisibleReadings.Current,
        },
        hasCupertinoPane: {
            type: Boolean,
            default: false,
        },
        projectId: {
            type: Number,
            required: false,
        },
    },
    filters: {
        integer: (value) => {
            if (!value) return "";
            return Math.round(value);
        },
    },
    watch: {
        station(this: any) {
            if (this.cupertinoPane) {
                this.cupertinoPane.present({ animate: true });
            }
        },
    },
    data(): {
        viewingSummary: boolean;
        sensorMeta: SensorMeta | null;
        StationStatus: any;
        isMobileView: boolean;
        cupertinoPane: CupertinoPane | null;
    } {
        return {
            viewingSummary: true,
            sensorMeta: null,
            StationStatus: StationStatus,
            isMobileView: window.screen.availWidth < 500,
            cupertinoPane: null,
        };
    },
    async mounted() {
        if (this.hasCupertinoPane && this.isMobileView) {
            this.initCupertinoPane();
        }
        this.sensorMeta = await this.sensorDataQuerier.querySensorMeta();
    },
    destroyed() {
        this.destroyCupertinoPane();
    },
    computed: {
        resolvedProjectId(): number | undefined {
            if (this.projectId != null) {
                return this.projectId;
            }
            if (!this.$router) {
                return undefined;
            }
            const id = this.$route.params.projectId || this.$route.params.id;
            return id ? Number(id) : undefined;
        },
        visibleSensor(): ModuleSensorMeta | null {
            const primarySensor = this.station.primarySensor;
            if (this.sensorMeta && primarySensor) {
                return this.sensorMeta.findSensorByKey(primarySensor.fullKey);
            }
            return null;
        },
        hasData(): boolean {
            return this.station.hasData;
        },
        decoratedReading(): DecoratedReading | null {
            const readings = this.station.getDecoratedReadings(this.visibleReadings);
            if (readings && readings.length > 0) {
                return readings[0];
            }
            return null;
        },
        visibleReadingValue(): number | null {
            const reading: DecoratedReading | null = this.decoratedReading;
            if (reading) {
                return reading.value;
            }
            return null;
        },
        latestPrimaryLevel(): any {
            const reading: DecoratedReading | null = this.decoratedReading;
            if (reading) {
                return reading?.thresholdLabel;
            }
            return null;
        },
        latestPrimaryColor(): string {
            if (isCommunityStation(this.station)) {
                return "rgb(92, 107, 192)";
            } else if (isDownStation(this.station)) {
                return "black";
            } else if (isRetiredStation(this.station)) {
                return "#ABABAB";
            }

            const reading: DecoratedReading | null = this.decoratedReading;
            if (reading) {
                return reading?.color;
            }
            return getPartnerCustomizationWithDefault().latestPrimaryNoDataColor;
        },
        dotColorClass(): string {
            if (this.isCommunityStation(this.station)) return "dot-community";
            if (this.isDownStation(this.station)) return "dot-down";
            if (this.isRetiredStation(this.station)) return "dot-offline";
            return "dot-default";
        },
        formatReadingForDisplay(): string {
            if (this.visibleReadingValue === null || this.visibleReadingValue === undefined) {
                return "-";
            }
            const filter = Vue.filter("prettyReadingNarrowSpace");
            return filter ? filter(this.visibleReadingValue) : this.visibleReadingValue.toString();
        },
        dotSizeClass(): string {
            const text = this.formatReadingForDisplay;
            if (!text.includes(".")) return "";
            return text.length > 3 ? "value-decimal-long" : "value-decimal-short";
        },
    },
    methods: {
        isDownStation,
        isRetiredStation,
        isCommunityStation,
        getDotColor() {
            if (isCommunityStation(this.station)) {
                return "#5c6bc0";
            } else if (isDownStation(this.station)) {
                return "#000";
            } else if (isRetiredStation(this.station)) {
                return "#ABABAB";
            }

            const reading = this.decoratedReading;
            if (reading) {
                return reading.color;
            }
            return getPartnerCustomizationWithDefault().latestPrimaryNoDataColor;
        },
        getDotTextColor() {
            return isDownStation(this.station) && !isCommunityStation(this.station) ? "#fff" : "#000";
        },
        getDotStyle() {
            if (isCommunityStation(this.station)) {
                return { "background-color": "rgb(92, 107, 192)" };
            } else if (isDownStation(this.station)) {
                return { "background-color": "black" };
            } else if (isRetiredStation(this.station)) {
                return { "background-color": "#ABABAB" };
            }

            const reading = this.decoratedReading;
            if (reading) {
                return { "background-color": reading.color };
            }
            return { "background-color": getPartnerCustomizationWithDefault().latestPrimaryNoDataColor };
        },
        viewSummary() {
            this.viewingSummary = true;
        },
        onClickExplore() {
            const bm = BookmarkFactory.forStation(this.station.id, this.exploreContext);
            const bookmark = serializeBookmark(bm);
            if (this.$router) {
                return this.$router.push({
                    name: "exploreBookmark",
                    query: { bookmark },
                });
            }
            const query = new URLSearchParams({ bookmark });
            window.open(withBasePath(`/dashboard/explore?${query.toString()}`), "_blank");
        },
        getBatteryIcon() {
            return this.$loadAsset(getBatteryIcon(this.station.battery));
        },
        wantCloseSummary() {
            this.$emit("close");
        },
        openStationPageTab() {
            let href: string;
            if (this.$router) {
                const routeName = this.resolvedProjectId ? "viewStation" : "viewStationFromMap";
                const params: Record<string, string> = { stationId: String(this.station.id) };
                if (this.resolvedProjectId) {
                    params.projectId = String(this.resolvedProjectId);
                }
                href = this.$router.resolve({ name: routeName, params }).href;
            } else if (this.resolvedProjectId) {
                href = withBasePath(`/dashboard/projects/${this.resolvedProjectId}/station/${this.station.id}`);
            } else {
                href = withBasePath(`/station/${this.station.id}`);
            }
            window.open(href, "_blank");
        },
        interpolatePartner(baseString) {
            return interpolatePartner(baseString);
        },
        isPartnerCustomisationEnabled(): boolean {
            return isCustomisationEnabled();
        },
        async initCupertinoPane(): Promise<void> {
            const paneContentEl = this.$refs["paneContent"] as HTMLDivElement;
            const generalRowEl = (this.$refs["summaryContent"] as Vue).$refs["summaryGeneralRow"] as HTMLDivElement;
            this.cupertinoPane = new CupertinoPane(".js-cupertinoPane", {
                parentElement: "body",
                breaks: {
                    top: { enabled: true, height: paneContentEl.scrollHeight, bounce: true },
                    // add padding top of container and margin of general row
                    middle: { enabled: true, height: generalRowEl.scrollHeight + 25 + 10, bounce: true },
                    bottom: { enabled: true, height: 0 },
                },
                bottomClose: true,
                buttonDestroy: false,
            });
            this.cupertinoPane.present({ animate: true });
        },
        destroyCupertinoPane(): void {
            if (this.cupertinoPane) {
                this.cupertinoPane.destroy();
            }
        },
    },
});
</script>

<style scoped lang="scss">
@use "src/scss/mixins";
@use "src/scss/variables";

.station-hover-summary {
    position: absolute;
    background-color: #ffffff;
    border: solid 1px #d8dce0;
    border-radius: 3px;
    z-index: 2;
    display: flex;
    flex-direction: column;
    padding: 27px 20px 17px;
    width: 389px;
    box-sizing: border-box;

    ::v-deep .station-name {
        font-size: 16px;
    }

    * {
        font-family: variables.$font-family-light;
    }

    @include mixins.bp-down(variables.$xs) {
        width: 100% !important;
        left: 0 !important;
        top: 0 !important;
        transform: translate(0, 0) !important;
        border-radius: 10px;
        padding: 25px 10px 12px 10px;

        .close-button {
            display: none;
        }

        .navigate-button {
            width: 14px;
            height: 14px;
            right: -3px;
            top: -17px;
        }

        .image-container {
            flex-basis: 62px;
            height: 62px;
            margin-right: 10px;
        }

        .station-name {
            font-size: 14px;
        }

        .explore-button {
            margin-top: 15px;
            margin-bottom: 10px;
        }
    }
}

.location-coordinates {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    flex-basis: 50%;
}

.location-coordinates .coordinate {
    display: flex;
    flex-direction: column;

    > div {
        margin-bottom: 2px;

        &:nth-of-type(2) {
            font-size: 12px;
        }
    }
}

.close-button {
    cursor: pointer;
    @include mixins.position(absolute, -7px -5px null null);
}

.navigate-button {
    cursor: pointer;
    @include mixins.position(absolute, -10px 20px null null);
}

.readings-container {
    margin-top: 7px;
    padding-top: 9px;
    border-top: 1px solid #f1eeee;
    text-align: left;
    font-size: 14px;
    color: #2c3e50;
}

.readings-container div.title {
    padding-bottom: 13px;
    font-family: var(--font-family-medium);

    body.floodnet & {
        font-family: var(--font-family-bold);
    }
}

.explore-button {
    font-size: 18px;
    font-family: var(--font-family-bold);
    color: #ffffff;
    text-align: center;
    padding: 10px;
    margin: 24px 0 14px 0px;
    background-color: var(--color-secondary);
    border: 1px solid rgb(215, 220, 225);
    border-radius: 4px;
    cursor: pointer;
}

.icon {
    padding-right: 7px;
}

::v-deep .reading {
    height: 35px;

    .name {
        font-size: 11px;
    }
}

.latest-primary {
    font-size: 12px;
    font-family: variables.$font-family-bold;
    @include mixins.flex(center, flex-end);

    @include mixins.bp-down(variables.$xs) {
        margin-top: 10px;
    }

    i {
        font-style: normal;
        font-size: 10px;
        font-family: variables.$font-family-bold;
        border-radius: 50%;
        height: 22px;
        width: 22px;
        padding: 0 4px;
        color: #fff;
        margin-left: 5px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        line-height: 1;

        &.value-decimal-short {
            height: 24px;
            min-width: 24px;
        }

        &.value-decimal-long {
            height: 26px;
            min-width: 26px;
            font-size: 9px;
        }

        &.community-dot {
            width: 16px;
            height: 16px;
            border-radius: 2px;
            padding: 0;
        }
        &.down-dot,
        &.dot-offline {
            width: 16px;
            height: 16px;
            padding: 0;
        }
        &.dot-community {
            background-color: rgb(92, 107, 192);
        }
        &.dot-down {
            background-color: black;
        }
        &.dot-offline {
            background-color: #ababab;
        }
        &.dot-default {
            background-color: var(--color-secondary, #00ccff);
        }
    }

    .no-data {
        color: var(--color-dark);
        font-family: variables.$font-family-bold;

        body.floodnet & {
            color: #cccccc;
        }
    }
}
</style>
